<template>
  <div v-hotkey="shortcuts" class="journal" @contextmenu.prevent>
    <FindBar
      v-if="isFindBarOpen"
      ref="findBarEl"
      container-id="scrollable-document"
      @close="closeFindBar"
    />
    <ObjectTopBar
      :object-id="journalEntryId"
      :database="database"
      @find="openFindBar"
    >
      <template #left>
        <div class="datepicker-container">
          <p ref="dateEl" class="date" @click="handleDateClick">
            {{ dateDisplay }}
          </p>
          <div class="nav">
            <BaseButton
              kind="auxiliary"
              type="secondary"
              :tooltip="{
                text: 'Previous day',
                shortcut: navigationActions.previousPeriod.shortcutsDisplay,
              }"
              tooltip-display="always"
              @click.stop="goToPreviousDay"
            >
              <template #icon>
                <ChevronLeftIcon />
              </template>
            </BaseButton>
            <BaseButton
              kind="auxiliary"
              type="secondary"
              tooltip-display="always"
              :tooltip="{
                text: 'Next day',
                shortcut: navigationActions.nextPeriod.shortcutsDisplay,
              }"
              @click.stop="goToNextDay"
            >
              <template #icon>
                <ChevronRightIcon />
              </template>
            </BaseButton>
            <BaseButton
              v-if="!isToday"
              tooltip-display="always"
              :tooltip="{
                text: 'Today',
                shortcut: navigationActions.goToToday.shortcutsDisplay,
              }"
              kind="auxiliary"
              type="secondary"
              @click="goToToday"
            >
              <template #icon>
                <BackIcon />
              </template>
            </BaseButton>
          </div>
        </div>
      </template>
    </ObjectTopBar>

    <div v-if="isMounted" class="journal-content">
      <div class="document-section">
        <slot />
      </div>
    </div>
    <DatePicker ref="datePickerRef" :modes="['date']" :show-clear="false" />
  </div>
</template>

<script lang="ts">
import {
  addDays,
  isToday as dateIsToday,
  format,
  getYear,
  startOfToday,
} from "date-fns";
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  toRefs,
} from "vue";
import { useRoute } from "vue-router";

import { navigationActions } from "@/components/actions/actions";
import BaseButton from "@/components/base/BaseButton.vue";
import DatePicker from "@/components/DatePicker.vue";
import FindBar from "@/components/find/FindBar.vue";
import { useFindBar } from "@/components/find/useFindBar";
import ObjectTopBar from "@/components/graph/CustomObjectTopBar.vue";
import BackIcon from "@/components/icons/BackIcon.vue";
import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import * as inject from "@/components/inject";
import {
  assignShortcuts,
  goToNextPeriod,
  goToNow,
  goToPreviousPeriod,
  Shortcuts,
} from "@/components/shortcuts";
import type { EntryRecord } from "@/composables/useJournalEntry";
import { RouteNames } from "@/routes/types";
import { hotkeys } from "@/utils/hotkeys";
import { navigateToJournal } from "@/utils/navigate";

export default defineComponent({
  name: "Journal",
  components: {
    BaseButton,
    DatePicker,
    BackIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    FindBar,
    ObjectTopBar,
  },
  props: {
    date: {
      type: Object as PropType<Date>,
      required: true,
    },
    value: {
      type: [Object, null] as PropType<EntryRecord | null>,
      required: false,
    },
  },
  setup(props) {
    const { date } = toRefs(props);

    // Mandatory due to BaseTopBar teleportation
    const isMounted = ref(false);

    const { isFindBarOpen, findBarEl, openFindBar, closeFindBar } =
      useFindBar();

    const router = inject.router();
    const route = useRoute();
    const clock = inject.clock();
    const databaseRef = inject.database();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();

    const currentDate = computed(() => clock.value?.getDate());
    const day = computed(() => date.value?.getDate() ?? 1);
    const isToday = computed(() => {
      if (!currentDate.value || !date.value) return false;
      return dateIsToday(date.value);
    });
    const showYear = computed(() => {
      const selectedYear = getYear(date.value);
      const todayYear = getYear(startOfToday());
      return selectedYear !== todayYear;
    });
    const dateDisplay = computed(() => {
      if (showYear.value) {
        return format(date.value, "LLLL d, yyyy");
      }
      return `${format(date.value, "LLLL")} ${day.value}`;
    });
    const dateEl = ref<HTMLElement>();
    const datePickerRef = ref<InstanceType<typeof DatePicker>>();

    const goToNextDay = () => {
      const newDate = addDays(date.value, 1);
      goToDate(newDate);
      return true;
    };

    const goToPreviousDay = () => {
      const newDate = addDays(date.value, -1);
      goToDate(newDate);
      return true;
    };

    const goToToday = () => {
      goToDate(startOfToday());
      return true;
    };

    const goToDate = (newDate: Date) => {
      // Preserve the org context when browsing dates on the org route.
      const orgId =
        route.name === RouteNames.OrgJournal &&
        typeof route.params.orgId === "string"
          ? route.params.orgId
          : undefined;
      navigateToJournal(router, newDate, undefined, orgId);
    };

    const handleDateClick = () => {
      if (datePickerRef.value && dateEl.value) {
        datePickerRef.value.showPicker(
          dateEl.value,
          date.value,
          (selection) => {
            if (selection?.mode === "date") {
              goToDate(selection.date);
            }
          }
        );
      }
    };

    onMounted(() => {
      isMounted.value = true;
    });

    const database = computed(() => databaseRef.value);
    const journalEntryId = computed(() => props.value?.id);

    const shortcuts = computed<Shortcuts>(() => {
      const injection = { isModalShown, isInputFocused };
      const baseShortcuts = {
        ...assignShortcuts(injection, goToNow, goToToday),
        ...assignShortcuts(injection, goToNextPeriod, () => goToNextDay()),
        ...assignShortcuts(injection, goToPreviousPeriod, () =>
          goToPreviousDay()
        ),
      };
      return baseShortcuts;
    });

    return {
      closeFindBar,
      day,
      dateDisplay,
      dateEl,
      datePickerRef,
      findBarEl,
      isFindBarOpen,
      openFindBar,
      format,
      formatHotkey: hotkeys.format.stringify,
      goToDate,
      goToToday,
      goToNextDay,
      goToPreviousDay,
      handleDateClick,
      isMounted,
      isToday,
      navigationActions,
      shortcuts,
      database,
      journalEntryId,
    };
  },
});
</script>

<style scoped lang="scss">
.datepicker-container {
  display: flex;
  flex-direction: row;
  column-gap: 4px;
  align-items: center;
  &:hover .nav {
    opacity: 1;
  }

  .date {
    @extend %font-body-regular;
    margin: 0;
    padding: 6px 8px;
    margin-inline-start: 4px;
    border-radius: $border-radius-m;
    @include transition(background-color 100ms ease-in);

    &:hover {
      @include themed() {
        background-color: t($bg-menu-hover);
      }
    }
  }

  .nav {
    display: flex;
    flex-direction: row;
    column-gap: 2px;
    @include transition(opacity 100ms ease-in);
  }
}

.journal {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: clip;
  // Anchor for the find bar, which must not scroll with the entry.
  position: relative;

  .journal-content {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    overflow: hidden;

    .document-section {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 32px;
      padding-top: 70px;
      position: relative;
    }
  }
}
</style>
